import { useCallback, useEffect, useState } from 'react'

import { getSelfCheckinCardReader, postSelfCheckinCardReader } from '@/apis/aipass'
import { selfCardPCIDStorageKey, serialNumberStorageKey } from '@/constants/self-checkin-device-storage'
import { useDeviceSettingForm } from '@/hooks/self-checkin-device-settings/use-device-setting-form'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { CardReaderCommand, SelfCheckinCardReaderType } from '@/models/self-checkin/card-reader'

export type CardReaderSettingFormValues = {
  serialNumber: string
  pcId: string
}

export const useCardReaderSetting = (hotelId: string) => {
  const { errorHandler } = useErrorHandler()
  const [checkResult, setCheckResult] = useState<SelfCheckinCardReaderType>()
  const [checkProcessId, setCheckProcessId] = useState<string>()
  const { control, isDirty, isLoading, setIsLoading, handleSubmit, reset, cancel } = useDeviceSettingForm(getStoredCardReaderSetting)

  const fetchSetting = useCallback(
    async (processId: string) => {
      const storedSerialNumber = localStorage.getItem(serialNumberStorageKey)
      if (!storedSerialNumber) {
        return
      }
      const storedPcId = localStorage.getItem(selfCardPCIDStorageKey)
      const responses = await getSelfCheckinCardReader(hotelId, storedSerialNumber, storedPcId, processId)
      const result = responses?.find(response => response.command === CardReaderCommand.CheckSerialNumber)
      result && setCheckResult(result)
    },
    [hotelId],
  )

  const startConnectionCheck = useCallback(
    async (serialNumber: string, pcId: string | null) => {
      setCheckResult({
        serial_number: serialNumber,
        command: CardReaderCommand.CheckSerialNumber,
        message: '接続確認中......',
        process_id: '',
        is_success: true,
      })
      const { processId } = await postSelfCheckinCardReader(hotelId, serialNumber, pcId, CardReaderCommand.CheckSerialNumber)
      setCheckProcessId(processId)
    },
    [hotelId],
  )

  const saveValues = useCallback(
    async (values: CardReaderSettingFormValues) => {
      try {
        setIsLoading(true)
        setCheckResult(undefined)
        localStorage.setItem(serialNumberStorageKey, values.serialNumber)
        localStorage.setItem(selfCardPCIDStorageKey, values.pcId)
        reset(values)
        if (values.serialNumber) {
          await startConnectionCheck(values.serialNumber, values.pcId)
        }
      } catch (error) {
        errorHandler(error)
      } finally {
        setIsLoading(false)
      }
    },
    [errorHandler, reset, setIsLoading, startConnectionCheck],
  )

  useEffect(() => {
    if (!checkProcessId) {
      return
    }
    const intervalId = window.setInterval(() => {
      fetchSetting(checkProcessId).catch(errorHandler)
    }, 3000)
    return () => window.clearInterval(intervalId)
  }, [checkProcessId, errorHandler, fetchSetting])

  useEffect(() => {
    const storedSetting = getStoredCardReaderSetting()
    reset(storedSetting)
    if (storedSetting.serialNumber) {
      startConnectionCheck(storedSetting.serialNumber, storedSetting.pcId).catch(errorHandler)
    }
  }, [errorHandler, reset, startConnectionCheck])

  return {
    control,
    isDirty,
    isLoading,
    checkResult,
    save: handleSubmit(saveValues),
    cancel,
  }
}

const getStoredCardReaderSetting = (): CardReaderSettingFormValues => ({
  serialNumber: localStorage.getItem(serialNumberStorageKey) || '',
  pcId: localStorage.getItem(selfCardPCIDStorageKey) || '',
})
